import type { ComputedRef } from 'vue';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';

export type AccessLevel = 'guest' | 'registered' | 'verified';

export const useAccessLevel = (): ComputedRef<AccessLevel> => {
  const authStore = useAuthStore();

  // ── Hydration-safety gate ───────────────────────────────────────────────
  // Report 'guest' until the component has MOUNTED on the client, then upgrade
  // to the real tier on the next tick. This guarantees the SSR render and the
  // first client (hydration) render are BOTH 'guest', so the access-gated tree
  // (commission / contacts / availability — LockedCard vs the verified blocks)
  // is identical across the hydration boundary. Any divergence makes Vue
  // mis-patch and CORRUPT the vnode tree: the verified <CommissionBlock> gets
  // grafted onto the guest <LockedCard>'s nodes, which both renders garbage AND
  // leaves a dangling child vnode that throws "Cannot read properties of null
  // (reading 'type')" when the route transition later unmounts the page.
  //
  // `hydrated` starts false on the SERVER TOO — we must NOT assume SSR is
  // 'guest' just because `user` lives in localStorage: the auth `token` is
  // mirrored into a cookie that IS readable on the server (see store/auth.ts),
  // so SSR could otherwise compute a non-guest tier and diverge from the gated
  // client. SSR never runs onMounted, so it stays 'guest' for the whole
  // render — exactly the public view we want in the SSR HTML for SEO.
  const hydrated = ref(false);
  if (import.meta.client) {
    if (getCurrentInstance()) {
      onMounted(() => {
        hydrated.value = true;
      });
    } else {
      // Called outside a component setup (no mount hook available) — we can't
      // defer, so accept the real tier immediately.
      hydrated.value = true;
    }
  }

  return computed<AccessLevel>(() => {
    if (!hydrated.value) return 'guest';
    if (!authStore.token || !authStore.user) return 'guest';
    if (!authStore.user.phoneVerified) return 'registered';
    return 'verified';
  });
};
